<!-- src/components/FilterMeals.vue -->
<script setup lang="ts">
import { ref } from 'vue'
import { showValidationError } from '../utils/errorHandling'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
dayjs.extend(isoWeek)

const props = defineProps<{ showSortAndPresets?: boolean; initialFrom?: string; initialTo?: string }>()

// Default date range for "All meals" mode (current week)
const defaultStart = dayjs().startOf('isoWeek').format('YYYY-MM-DD')
const defaultEnd = dayjs().endOf('isoWeek').format('YYYY-MM-DD')

const start_date = ref<string>(props.initialFrom || defaultStart)
const end_date = ref<string>(props.initialTo || defaultEnd)

// Mode: "upcoming" shows active meals from today onward; "all" browses everything by date range.
type Mode = 'upcoming' | 'all'
const mode = ref<Mode>(props.initialFrom && props.initialTo ? 'all' : 'upcoming')

type SortOption = 'date-asc' | 'date-desc'
const sortBy = ref<SortOption>('date-asc')

const emit = defineEmits<{
  (e: 'filter', payload: { start_date: string; end_date: string; show_active: boolean }): void
  (e: 'sort', sortOption: SortOption): void
}>()

const buildPayload = () => {
  if (mode.value === 'upcoming') {
    return {
      start_date: dayjs().startOf('day').format('DD/MM/YYYY'),
      end_date: dayjs().add(1, 'year').format('DD/MM/YYYY'),
      show_active: true,
    }
  }
  return {
    start_date: dayjs(start_date.value, 'YYYY-MM-DD').format('DD/MM/YYYY'),
    end_date: dayjs(end_date.value, 'YYYY-MM-DD').format('DD/MM/YYYY'),
    show_active: false,
  }
}

const setMode = (m: Mode) => {
  if (mode.value === m) return
  mode.value = m
  emit('filter', buildPayload())
}

const applyFilter = () => {
  if (!start_date.value || !end_date.value) {
    showValidationError('Please select both start and end dates.')
    return
  }
  emit('filter', buildPayload())
}

const clearFilter = () => {
  start_date.value = defaultStart
  end_date.value = defaultEnd
  sortBy.value = 'date-asc'
  emit('filter', buildPayload())
  emit('sort', sortBy.value)
}

// Quick preset functions (only used in "All meals" mode)
const setThisWeek = () => {
  start_date.value = dayjs().startOf('isoWeek').format('YYYY-MM-DD')
  end_date.value = dayjs().endOf('isoWeek').format('YYYY-MM-DD')
  applyFilter()
}

const setThisMonth = () => {
  start_date.value = dayjs().startOf('month').format('YYYY-MM-DD')
  end_date.value = dayjs().endOf('month').format('YYYY-MM-DD')
  applyFilter()
}

const setLastWeek = () => {
  start_date.value = dayjs().subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD')
  end_date.value = dayjs().subtract(1, 'week').endOf('isoWeek').format('YYYY-MM-DD')
  applyFilter()
}

const setLastMonth = () => {
  start_date.value = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
  end_date.value = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
  applyFilter()
}

const setToday = () => {
  start_date.value = dayjs().startOf('day').format('YYYY-MM-DD')
  end_date.value = dayjs().endOf('day').format('YYYY-MM-DD')
  applyFilter()
}

const setTomorrow = () => {
  start_date.value = dayjs().add(1, 'day').startOf('day').format('YYYY-MM-DD')
  end_date.value = dayjs().add(1, 'day').endOf('day').format('YYYY-MM-DD')
  applyFilter()
}
</script>

<template>
  <!-- Mode segmented control -->
  <div class="flex flex-col gap-2 mb-4">
    <label class="block text-sm font-medium text-gray-700">Meals to show</label>
    <div class="relative inline-flex rounded-full p-1 toggle-track self-start">
      <div class="absolute top-1 bottom-1 left-1 rounded-full bg-white pointer-events-none toggle-indicator"
        :style="{ transform: mode === 'all' ? 'translateX(100%)' : 'translateX(0)' }">
      </div>
      <button type="button" @click="setMode('upcoming')"
        class="relative z-10 rounded-full py-1.5 text-xs font-bold text-center transition-colors duration-200 toggle-btn toggle-btn--mode"
        :class="{ 'toggle-btn--active': mode === 'upcoming' }">
        Upcoming
      </button>
      <button type="button" @click="setMode('all')"
        class="relative z-10 rounded-full py-1.5 text-xs font-bold text-center transition-colors duration-200 toggle-btn toggle-btn--mode"
        :class="{ 'toggle-btn--active': mode === 'all' }">
        All meals
      </button>
    </div>
    <p class="text-sm text-gray-500">
      <template v-if="mode === 'upcoming'">
        Showing upcoming meals scheduled from today onward.
      </template>
      <template v-else>
        Browsing all meals, including past ones. Use the date range below to narrow your search.
      </template>
    </p>
  </div>

  <!-- Date range + actions — only in "All meals" mode -->
  <div v-if="mode === 'all'" class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
    <!-- Start Date -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
      <input type="date" v-model="start_date"
        class="form-field min-h-[44px] text-[16px] sm:text-base desktop-focus-ring w-full" />
    </div>

    <!-- End Date + action buttons -->
    <div class="flex flex-col sm:flex-row gap-3 sm:items-end">
      <div class="flex-1">
        <label class="block text-sm font-medium text-gray-700 mb-2">End Date</label>
        <input type="date" v-model="end_date"
          class="form-field min-h-[44px] text-[16px] sm:text-base desktop-focus-ring w-full" />
      </div>
      <div class="flex gap-3">
        <button @click="applyFilter"
          class="btn-primary mobile-touch-target desktop-button-hover desktop-focus-ring whitespace-nowrap flex-1 sm:flex-none"
          tabindex="0">
          Apply Filter
        </button>
        <button @click="clearFilter"
          class="btn-secondary mobile-touch-target desktop-button-hover desktop-focus-ring whitespace-nowrap flex-1 sm:flex-none"
          tabindex="0">
          Clear Filter
        </button>
      </div>
    </div>
  </div>

  <!-- Sort Options and Quick Presets — "All meals" mode + list view only -->
  <div v-if="mode === 'all' && props.showSortAndPresets !== false" class="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
    <!-- Sorting Toggle (Left side on desktop, top on mobile) -->
    <div class="lg:order-1">
      <div class="flex flex-wrap gap-4 items-start">
        <!-- Sort By -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
          <div class="relative inline-flex rounded-full p-1 toggle-track">
            <div class="absolute top-1 bottom-1 left-1 rounded-full bg-white pointer-events-none toggle-indicator"
              :style="{ transform: sortBy === 'date-asc' ? 'translateX(100%)' : 'translateX(0)' }">
            </div>
            <button type="button" @click="sortBy = 'date-desc'; emit('sort', 'date-desc')"
              class="relative z-10 rounded-full py-1.5 text-xs font-bold text-center transition-colors duration-200 toggle-btn toggle-btn--72"
              :class="{ 'toggle-btn--active': sortBy === 'date-desc' }">
              Latest
            </button>
            <button type="button" @click="sortBy = 'date-asc'; emit('sort', 'date-asc')"
              class="relative z-10 rounded-full py-1.5 text-xs font-bold text-center transition-colors duration-200 toggle-btn toggle-btn--72"
              :class="{ 'toggle-btn--active': sortBy === 'date-asc' }">
              Earliest
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Presets (Right side on desktop, bottom on mobile) -->
    <div class="lg:order-2">
      <label class="block text-sm font-medium text-gray-700 mb-2">Quick Presets</label>
      <div class="flex flex-wrap gap-2">
        <button @click="setToday" class="filter-btn mobile-touch-target desktop-button-hover desktop-focus-ring"
          tabindex="0">
          Today
        </button>
        <button @click="setTomorrow" class="filter-btn mobile-touch-target desktop-button-hover desktop-focus-ring"
          tabindex="0">
          Tomorrow
        </button>
        <button @click="setThisWeek" class="filter-btn mobile-touch-target desktop-button-hover desktop-focus-ring"
          tabindex="0">
          This Week
        </button>
        <button @click="setLastWeek" class="filter-btn mobile-touch-target desktop-button-hover desktop-focus-ring"
          tabindex="0">
          Last Week
        </button>
        <button @click="setThisMonth" class="filter-btn mobile-touch-target desktop-button-hover desktop-focus-ring"
          tabindex="0">
          This Month
        </button>
        <button @click="setLastMonth" class="filter-btn mobile-touch-target desktop-button-hover desktop-focus-ring"
          tabindex="0">
          Last Month
        </button>
      </div>
      <div class="mt-2 text-sm text-gray-600">
        Click a preset to quickly set the date range, then apply the filter
      </div>
    </div>
  </div>
</template>

<style scoped>
.toggle-track     { background: #EEEEEE; }
.toggle-indicator { width: calc(50% - 4px); box-shadow: 0 1px 2px rgba(0,0,0,.08); transition: transform 200ms cubic-bezier(0.4,0,0.2,1); }
.toggle-btn         { color: rgba(63,74,60,.6); }
.toggle-btn--72     { width: 72px; }
.toggle-btn--mode   { width: 92px; }
.toggle-btn--active { color: #4F8646; }
</style>
